import { ProviderDetail } from 'models/provider';
import { MhpDetails } from 'modules/types/common';

export enum VisitType {
  OUTPATIENT = 'outpatient',
  INPATIENT = 'inpatient',
  EMERGENCY = 'emergency',
  LEGACY = 'legacy'
}

export interface OptionType {
  label: string;
  value: 'asc' | 'desc';
}

export enum FilterKey {
  START_DATE = 'startDate',
  END_DATE = 'endDate',
  YEAR = 'year',
  PROVIDER = 'provider',
  LOCATION = 'location',
  VISIT_TYPE = 'visitType'
}

export enum ActionButton {
  DOWNLOAD,
  SHARE
}

export interface Selection {
  [key: number]: boolean;
}

export interface FilterItem {
  value: string;
  label?: string;
  checked?: boolean;
}

export interface SortOptions {
  key: number;
  label: string;
  selected: boolean;
  value: string;
}

export interface VisitSummaryFilter {
  provider: string;
  location: string;
  visitType: VisitType | string;
  startDate: string;
  endDate: string;
  year?: string | null;
}

export interface PrimaryProvider {
  providerName: string;
  providerId: string;
  corpProviderId: string;
  providerNcid: string;
  providerType: string;
  providerTypeNcid: string;
  providerSpecialty: string;
  providerImageUrl: string;
  providerPhoneNumber: string;
  isSelectHealth: boolean;
  isAttendingPhysician: boolean;
}

export interface LocationObject {
  locationName: string;
  locationId: string;
  locationNcid?: string;
}

export interface VisitSummary {
  chiefComplaint?: {
    valueString: string;
  };
  id: string;
  date: string;
  locationObject: LocationObject;
  visitType: VisitType;
  visitDescription?: string;
  primaryProvider?: PrimaryProvider;
  patient: string;
  source: string;
  fin: string;
  status: string;
  isSelected?: boolean;
  mhpDetails?: MhpDetails;
  eventId?: string;
}

export interface WeDiscussedItem {
  id: string;
  sections: {
    assessmentAndPlan?: string;
    historyOfPresentIllness?: string;
    notes?: string;
  };
  status: string;
}

export interface WeDiscussedDiagnosis {
  id: string;
  name: string;
  patient: string;
  onsetDate: string;
  provider: string;
  primaryProvider: ProviderDetail;
  status: string;
  category: string;
  confirmation: string;
  comments: null | string;
  lastUpdated: string;
}

export interface VisitSummaryDetails {
  id: string;
  patient: string;
  date: string;
  location: string;
  locationObject: LocationObject;
  provider: string;
  primaryProvider: PrimaryProvider;
  visitType: string;
  chiefComplaint: ChiefComplaint;
  conditions: {};
  diagnoses?: WeDiscussedDiagnosis[];
  procedures: Procedure[];
  lastUpdated: string;
  source: string;
  allProviders: {};
  admitDate: string;
  dischargeDate: string;
  dischargeDisposition: string;
  weDiscussed?: WeDiscussedItem[];
  documents: {
    patientEducation: ResourceDocument;
    officeVisitNotes: ResourceDocument;
    inpatientDocumentInfoNotes: ResourceDocument;
    otherVisitNotes: ResourceDocument;
  };
  visitDescription: string;
  inpatientDocumentInfo: InpatientDocumentInfoItem[];
  fin: string;
}

export interface InpatientDocumentInfoItem {
  id: string;
  sections: {
    followUp: string;
    dischargeDiagnosisAndPlan: string;
    hospitalCourse: string;
  };
  status: string;
  document: Document;
}

export interface ChiefComplaint {
  id: string;
  dateEffective: Date;
  status: string;
  valueString: string;
  lastUpdated: Date;
}

export interface Procedure {
  id: string;
  name: string;
  encounter: {};
  code: string;
  performer: {};
  datePerformed: string;
  status: string;
  location: string;
  notes: {};
  lastUpdated: string;
}

export interface ResourceDocument {
  categoryDisplay: string;
  documents: Document[];
}

export interface Document {
  createDate: string;
  indexDate: string;
  status: string;
  id: string;
  encounterId: string;
  type: [];
  authenticator: {};
  docStatus: string;
  description: string;
  links: DocumentLink[];
}

export interface DocumentLink {
  mimeType: string;
  documentId: string;
}

export interface BinaryDocument {
  resourceType: string;
  id: string;
  meta?: {};
  contentType: string;
  content: string;
}

export enum DocumentContentType {
  PDF = 'application/pdf',
  HTML = 'text/html'
}

export enum DocumentCategoryType {
  EDUCATION = 'Patient Education',
  OFFICE = 'Office Visit Notes',
  INPATIENT = 'Inpatient Document Information Notes',
  OTHER = 'Other Visit Notes'
}

export interface ShareVisitSummaryCcd {
  contactPhoneNumber: string;
  encounterIdList?: string[];
  message: string;
  subject?: string;
  toAddress: string;
  useSecureEmail: boolean;
}
